import axios, { AxiosError } from 'axios';
import { API_ORIGIN } from './api';

/**
 * Typed auth API client. Uses raw axios (not the shared `api` singleton)
 * because the singleton's 401 interceptor would redirect to landing
 * during pre-login probes.
 */
const client = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: true,
  timeout: 20_000,
});

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  pictureUrl: string | null;
  countryCode: string | null;
  primaryLocale: string | null;
  hankoImageUrl: string | null;
  hankoSealText: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  companyId: string | null;
  emailVerified: boolean;
}

interface AuthResponse {
  user: CurrentUser;
  requireVerification?: boolean;
}

export const authApi = {
  async register(input: { email: string; password: string; name?: string }): Promise<AuthResponse> {
    return (await client.post<AuthResponse>('/auth/register', input)).data;
  },
  async login(input: { email: string; password: string }): Promise<AuthResponse> {
    return (await client.post<AuthResponse>('/auth/login', input)).data;
  },
  async forgotPassword(email: string): Promise<{ ok: boolean }> {
    return (await client.post<{ ok: boolean }>('/auth/forgot-password', { email })).data;
  },
  async resetPassword(token: string, password: string): Promise<{ ok: boolean }> {
    return (await client.post<{ ok: boolean }>('/auth/reset-password', { token, password })).data;
  },
  async verifyEmail(token: string): Promise<AuthResponse> {
    return (await client.post<AuthResponse>('/auth/verify-email', { token })).data;
  },
  async resendVerification(): Promise<{ ok: boolean }> {
    return (await client.post<{ ok: boolean }>('/auth/resend-verification')).data;
  },
  async logout(): Promise<void> {
    await client.post('/auth/logout');
  },
  async me(): Promise<CurrentUser | null> {
    try {
      return (await client.get<CurrentUser>('/auth/me')).data;
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 401) return null;
      throw err;
    }
  },
  async updateProfile(input: { name?: string; pictureUrl?: string }): Promise<CurrentUser> {
    return (await client.patch<CurrentUser>('/auth/me', input)).data;
  },
  async uploadAvatar(file: Blob): Promise<CurrentUser> {
    const fd = new FormData();
    fd.append('file', file, 'avatar.png');
    return (await client.post<CurrentUser>('/auth/me/avatar', fd)).data;
  },
};

export function oauthHref(provider: 'google' | 'apple'): string {
  return `${API_ORIGIN}/auth/${provider}`;
}

/**
 * Best-effort extraction of a user-facing message from an axios error.
 * NestJS sends `{ statusCode, message, error }` for HttpExceptions.
 * Falls back to the generic copy passed in.
 */
export function readApiError(err: unknown, fallback: string): string {
  const ax = err as AxiosError<{ message?: string | string[] }>;
  const raw = ax?.response?.data?.message;
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (Array.isArray(raw) && raw.length) return String(raw[0]);
  return fallback;
}
